import { useEffect, useState, useCallback } from "react";
import { Search, MapPin, SlidersHorizontal, RotateCcw, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import JobCard from "../components/JobCard";
import { useLanguage } from "../i18n/LanguageContext";
import api from "../lib/api";

const TYPES = ["full_time", "part_time", "contract", "internship", "freelance", "remote"];
const CATEGORIES = ["tech", "marketing", "sales", "design", "finance", "hr", "health", "education", "other"];

export default function Jobs() {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [jobType, setJobType] = useState("");
  const [category, setCategory] = useState("");

  const fetchJobs = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs({ q, location, job_type: jobType, category });
  };

  const reset = () => {
    setQ(""); setLocation(""); setJobType(""); setCategory("");
    fetchJobs();
  };

  const selectClass = "w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
      <Navbar />

      {/* Search header */}
      <section className="border-b-2 border-slate-900 bg-sky-400 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{t("jobs.title")}</h1>
          <p className="mt-2 text-base font-medium text-slate-800">{t("jobs.subtitle")}</p>
          <form onSubmit={handleSearch} className="mt-6 flex flex-col gap-3 rounded-md border-2 border-slate-900 bg-white p-3 shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] md:flex-row" data-testid="jobs-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("jobs.searchPlaceholder")}
                data-testid="jobs-search-input"
                className="w-full rounded-md border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-slate-900 focus:outline-none"
              />
            </div>
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t("jobs.locationPlaceholder")}
                data-testid="jobs-location-input"
                className="w-full rounded-md border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-slate-900 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="jobs-search-button"
              className="rounded-full border-2 border-slate-900 bg-slate-900 px-8 py-2.5 font-heading text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(56,189,248,1)]"
            >
              {t("jobs.search")}
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-4">
        {/* Filters sidebar */}
        <aside className="h-fit rounded-md border-2 border-slate-900 bg-white p-6 lg:sticky lg:top-24" data-testid="jobs-filters">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-slate-900">
              <SlidersHorizontal className="h-4 w-4" strokeWidth={2} /> {t("jobs.filters")}
            </h2>
            <button onClick={reset} data-testid="jobs-reset-filters" className="flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline">
              <RotateCcw className="h-3 w-3" /> {t("jobs.reset")}
            </button>
          </div>
          <div className="mt-5 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.jobType")}</label>
              <select value={jobType} onChange={(e) => setJobType(e.target.value)} data-testid="jobs-filter-type" className={`${selectClass} mt-1.5`}>
                <option value="">{t("jobs.all")}</option>
                {TYPES.map((ty) => <option key={ty} value={ty}>{t(`jobTypes.${ty}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.category")}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="jobs-filter-category" className={`${selectClass} mt-1.5`}>
                <option value="">{t("jobs.all")}</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
              </select>
            </div>
            <button
              onClick={handleSearch}
              data-testid="jobs-apply-filters"
              className="w-full rounded-full border-2 border-slate-900 bg-sky-400 py-2 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,1)]"
            >
              {t("jobs.search")}
            </button>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-3">
          <p className="mb-5 text-sm font-semibold text-slate-600" data-testid="jobs-results-count">
            {jobs.length} {t("jobs.results")}
          </p>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
          ) : jobs.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-16 text-center text-sm text-slate-500" data-testid="jobs-no-results">
              {t("jobs.noResults")}
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2" data-testid="jobs-list">
              {jobs.map((job) => <JobCard key={job.job_id} job={job} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
